/**
 * /clients — coach-side roster on the web (ONB-021 minimal stub).
 *
 * R1.2 sub-step 4. Lists the coach's clients with status + step. Mirrors the
 * macOS ClientsListView for parity, but with a smaller surface — the macOS
 * app is the primary coach console. The web version exists so a coach who
 * only has a browser at hand can still see their roster + onboarding stage.
 *
 * Full per-client onboarding-stage progress (invite sent / registered /
 * commitment signed / KPI Pre-Assessment / Step 0 scheduled) — the proper
 * ONB-021 surface — lands in R1.x once we have the data points to show.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { ApiError } from '@/lib/api-client';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'loaded'; data: api.ClientListItem[] }
  | { kind: 'error'; message: string };

export function ClientsPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken, user } = useAuth();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    let active = true;
    if (!user) {
      setState({ kind: 'error', message: t('clients.errors.load') });
      return;
    }
    void (async () => {
      try {
        const list = await api.listClients(accessToken, { perPage: 100 });
        if (!active) return;
        setState({ kind: 'loaded', data: list.data });
      } catch (cause) {
        if (!active) return;
        if (cause instanceof ApiError) {
          setState({ kind: 'error', message: cause.envelope.message || t('clients.errors.load') });
        } else {
          setState({ kind: 'error', message: t('clients.errors.load') });
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, t]);

  return (
    <section className="max-w-5xl mx-auto px-s5 py-s7">
      <header className="mb-s5">
        <h1 className="font-display text-display-lg text-ink">{t('clients.title')}</h1>
        <p className="mt-s2 text-gray-2">{t('clients.subtitle')}</p>
      </header>

      {state.kind === 'loading' && (
        <p className="text-gray-2" data-testid="clients-loading">
          {t('common.labels.loading')}
        </p>
      )}

      {state.kind === 'error' && (
        <p role="alert" className="form-error" data-testid="clients-error">
          {state.message}
        </p>
      )}

      {state.kind === 'loaded' && state.data.length === 0 && (
        <article
          className="rounded-lg border border-bone-3 bg-bone-2 p-s6"
          data-testid="clients-empty"
        >
          <h2 className="font-display text-display-md text-ink">
            {t('clients.empty.headline')}
          </h2>
          <p className="mt-s3 text-gray-2">{t('clients.empty.body')}</p>
        </article>
      )}

      {state.kind === 'loaded' && state.data.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-bone-3 bg-bone-2">
          <table className="min-w-full text-sm">
            <thead className="bg-bone-3 text-ink">
              <tr>
                <Th>{t('clients.table.name')}</Th>
                <Th>{t('clients.table.email')}</Th>
                <Th>{t('clients.table.status')}</Th>
                <Th>{t('clients.table.currentStep')}</Th>
                <Th>{t('clients.table.createdAt')}</Th>
              </tr>
            </thead>
            <tbody data-testid="clients-tbody">
              {state.data.map((client) => (
                <tr key={client.id} className="border-t border-bone-3">
                  <Td className="font-medium text-ink">
                    {client.first_name} {client.last_name}
                  </Td>
                  <Td className="text-gray-2">{client.email}</Td>
                  <Td>
                    <span className="font-mono text-xs uppercase tracking-wide text-flame-deep">
                      {t(`clients.status.${client.status}`)}
                    </span>
                  </Td>
                  <Td className="font-mono tabular-nums text-gray-2">{client.current_step}</Td>
                  <Td className="text-gray-1">{client.created_at.slice(0, 10)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

interface CellProps {
  children: React.ReactNode;
  className?: string;
}

function Th({ children }: CellProps): JSX.Element {
  return (
    <th className="text-left px-s4 py-s2 font-medium text-xs uppercase tracking-wide">
      {children}
    </th>
  );
}

function Td({ children, className }: CellProps): JSX.Element {
  return <td className={`px-s4 py-s3 ${className ?? ''}`}>{children}</td>;
}
